import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import type { User } from '@/types/User'

/**
 * Role checks, in one place.
 *
 * The router already had `adminAuth`, `managerAuth` and `instructorAuth` meta
 * flags, but nothing used them and each tests a single role — so "admin or
 * manager", which is what every billing endpoint requires, could not be
 * expressed. This replaces that shape with a list.
 *
 * None of this is security. The API enforces the same rules and returns 403
 * regardless; hiding a link only saves someone walking into a dead end.
 */

/** Whether a user holds any of the named roles. Safe on a partially loaded user. */
export function userHasAnyRole(user: User | null | undefined, roles: string[]): boolean {
    if (!user || !Array.isArray(user.roles) || roles.length === 0) return false

    return user.roles.some((role) => roles.includes(role?.name))
}

export function useRoles() {
    const authStore = useAuthStore()

    const roles = computed(() => authStore.user?.roles?.map((role) => role?.name) ?? [])

    /** True when the signed-in user holds at least one of `names`. */
    function hasAnyRole(names: string[]): boolean {
        return userHasAnyRole(authStore.user, names)
    }

    /** Admin or manager: the pair that can see billing and gym-wide settings. */
    const canManageGym = computed(() => hasAnyRole(['admin', 'manager']))

    return { roles, hasAnyRole, canManageGym }
}
